import { useRef } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { DefaultTheme } from 'styled-components';
import styled from 'styled-components';
import { colors, spacing } from '../../style';
import { actionColor } from '../../style/theme-helpers';

export const sliderVariants = {
  action: actionColor,
  sun: colors.sun,
  positive: colors.positive,
};

const resolveSliderVariant = (variant: SliderVariant, props: { theme: DefaultTheme }) => {
  const value = sliderVariants[variant];
  return typeof value === 'function' ? value(props) : value;
};

export type SliderVariant = keyof typeof sliderVariants;

export const sliderSizes = {
  xs: {
    track: 4,
    thumb: 12,
    hoverIndication: 24,
  },
  sm: {
    track: 8,
    thumb: 16,
    hoverIndication: 24,
  },
  lg: {
    track: 20,
    thumb: 24,
    hoverIndication: 32,
  },
};

export type SliderSize = keyof typeof sliderSizes;

const StyledSlider = styled(SliderPrimitive.Root)<{ size: SliderSize }>`
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;
  touch-action: none;

  &[data-orientation='horizontal'] {
    height: ${({ size }) => sliderSizes[size].track}px;
  }

  &[data-orientation='vertical'] {
    width: ${({ size }) => sliderSizes[size].track}px;
    height: 100px;
    flex-direction: column;
  }
`;

const StyledTrack = styled(SliderPrimitive.Track)<{ size: SliderSize; variant: SliderVariant }>`
  background-color: ${colors.gray100};
  position: relative;
  flex-grow: 1;
  overflow: hidden;
  border-radius: 9999px;
  border: 1px solid ${colors.gray300};

  &[data-orientation='horizontal'] {
    height: ${({ size }) => sliderSizes[size].track}px;
  }

  &[data-orientation='vertical'] {
    width: ${({ size }) => sliderSizes[size].track}px;
  }
`;

const StyledRange = styled(SliderPrimitive.Range)<{ variant: SliderVariant }>`
  position: absolute;
  background-color: ${props => resolveSliderVariant(props.variant, props)};
  height: 100%;
`;

const StyledThumb = styled(SliderPrimitive.Thumb)<{ size: SliderSize }>`
  all: unset;
  display: block;
  width: ${({ size }) => sliderSizes[size].thumb}px;
  height: ${({ size }) => sliderSizes[size].thumb}px;
  background-color: white;
  box-shadow: 0 2px 4px -2px rgba(0, 0, 0, 0.32);
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid ${colors.gray400};

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(0 0 0 / 20%);
  }
`;

const StyledHoverIndication = styled.div<{ size: SliderSize; variant: SliderVariant }>`
  align-items: center;
  background: ${props => resolveSliderVariant(props.variant, props)};
  border-radius: 50% 50% 50% 0;
  color: white;
  display: flex;
  font-weight: 500;
  height: ${({ size }) => sliderSizes[size].hoverIndication}px;
  justify-content: center;
  position: relative;
  top: -${spacing.xs};
  transform: rotate(-45deg);
  width: ${({ size }) => sliderSizes[size].hoverIndication}px;
  z-index: 9999;

  .hover-indication {
    transform: rotate(45deg);
  }
`;

export interface SliderProps {
  /**
   * The minimum value of the slider
   * @defaultValue `1`
   */
  min?: number;
  /**
   * The maximum value of the slider
   * @defaultValue `10`
   */
  max?: number;
  /**
   * The step by which the slider value will change
   * @defaultValue `1`
   */
  step?: number;
  /**
   * The callback that will be called when the slider value changes
   */
  onChange: (num: number[]) => void;
  /**
   * Controlled value of the slider
   */
  value?: number[];
  /**
   * Default value of the slider
   */
  defaultValue?: number[];
  /**
   * The size of the slider
   * @defaultValue `sm`
   */
  size?: SliderSize;
  /**
   * The variant of the slider
   * @defaultValue `action`
   */
  variant?: SliderVariant;
  /**
   * Whether to show the teardrop value indicator
   * @defaultValue `true`
   */
  showIndicator?: boolean;
  /**
   * The id of the slider
   */
  id?: string;
  /**
   * Accessible label for the slider
   */
  'aria-label'?: string;
  /**
   * ID of the element that labels the slider
   */
  'aria-labelledby'?: string;
}

const Slider = ({
  min = 1,
  max = 10,
  step = 1,
  onChange,
  value,
  defaultValue,
  size = 'sm',
  variant = 'action',
  showIndicator = true,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
}: SliderProps) => {
  const valueToRender = defaultValue || value || [];

  const onValueChange = (newValue: number[]) => {
    onChange?.(newValue);
  };

  const thumbRef = useRef<HTMLSpanElement>(null);

  return (
    <StyledSlider
      id={id}
      defaultValue={defaultValue}
      max={max}
      min={min}
      onValueChange={onValueChange}
      size={size}
      step={step}
      value={value}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
    >
      <StyledTrack size={size} variant={variant}>
        <StyledRange variant={variant} />
      </StyledTrack>

      {valueToRender.map((value, index) => {
        return (
          <TooltipPrimitive.Provider key={index} delayDuration={0}>
            <TooltipPrimitive.Root>
              <TooltipPrimitive.Trigger
                asChild
                onClick={event => {
                  // Keep the hover indication open when clicking the thumb
                  event.preventDefault();
                }}
              >
                <StyledThumb data-testid="slider-thumb" ref={thumbRef} data-value={value} size={size} />
              </TooltipPrimitive.Trigger>
              {showIndicator ? (
                <TooltipPrimitive.Portal>
                  <TooltipPrimitive.Content
                    asChild
                    onPointerDownOutside={event => {
                      // Keep the hover indication open when sliding the thumb
                      if (event.target === thumbRef.current) {
                        event.preventDefault();
                      }
                    }}
                  >
                    <StyledHoverIndication size={size} variant={variant}>
                      <span className="hover-indication">{value}</span>
                    </StyledHoverIndication>
                  </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
              ) : undefined}
            </TooltipPrimitive.Root>
          </TooltipPrimitive.Provider>
        );
      })}
    </StyledSlider>
  );
};

Slider.displayName = 'Slider';

export default Slider;
